import React, {useEffect, useState} from 'react';
import Modal from 'react-modal';
import {uid} from 'uid';
import {rollPool, MAX_HUNGER} from '../../dice';
import {ROLL_TYPES, createRollRecord} from '../../rolls';
import {pushRoll} from '../../rollRepository';
import {vtmT} from '../../i18n';
import {VtmPoolResult} from '../VtmDice';
import '../../../styles/vtm/roll.css';

const Counter = ({id, label, value, onChange, min = 0, max = 30}) => (
  <div className="vtmField">
    <span className="vtmFieldLabel" id={`${id}-label`}>{label}</span>
    <div className="vtmStepper" role="group" aria-labelledby={`${id}-label`}>
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))} aria-label={`${label} −1`}>−</button>
      <output>{value}</output>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))} aria-label={`${label} +1`}>+</button>
    </div>
  </div>
);

/** Jet libre du MJ (PNJ, opposition) : réserve, dés de Soif et difficulté saisis directement. Caché par défaut. */
const VtmDmRollModal = ({isOpen, onClose, campaignUid, user}) => {
  const [npcName, setNpcName] = useState('');
  const [size, setSize] = useState(5);
  const [hunger, setHunger] = useState(0);
  const [difficulty, setDifficulty] = useState('');
  const [hidden, setHidden] = useState(true);
  const [record, setRecord] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) setRecord(null);
  }, [isOpen]);

  const launch = async () => {
    setBusy(true);
    try {
      const roll = rollPool({
        size,
        hunger: Math.min(hunger, size),
        difficulty: difficulty === '' ? undefined : Math.max(0, parseInt(difficulty, 10) || 0),
      });
      const name = npcName.trim() || vtmT('dm');
      const created = createRollRecord({
        id: uid(),
        type: ROLL_TYPES.POOL,
        character: {uid: 'dm', name, picture: null},
        userUid: user.uid,
        hidden,
        label: name,
        breakdown: [{label: vtmT('poolSize'), value: size, kind: 'base'}],
        regular: roll.regular,
        hunger: roll.hunger,
        difficulty: roll.difficulty,
        successes: roll.successes,
        outcome: roll.outcome,
        margin: roll.margin,
        messyIfWin: roll.messyIfWin,
        bestialIfFail: roll.bestialIfFail,
        autoFailure: roll.autoFailure,
      });
      await pushRoll(campaignUid, created);
      setRecord(created);
    } catch (e) {
      console.log(e);
    }
    setBusy(false);
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className="vtmModal" overlayClassName="vtmModalOverlay" contentLabel={vtmT('dmRoll')}>
      <div className="vtmModalHeader">
        <h2 className="vtmModalTitle">{vtmT('dmRoll')}</h2>
      </div>
      {!record && (
        <form className="vtmRollForm" onSubmit={event => { event.preventDefault(); launch(); }}>
          <p className="vtmModalHint">{vtmT('dmRollHint')}</p>
          <div className="vtmField">
            <label className="vtmFieldLabel" htmlFor="vtmNpcName">{vtmT('npcName')}</label>
            <input id="vtmNpcName" className="vtmTextInput" type="text" value={npcName} placeholder={vtmT('npcNamePlaceholder')} onChange={event => setNpcName(event.target.value)} />
          </div>
          <div className="vtmFieldRow">
            <Counter id="vtmDmSize" label={vtmT('poolSize')} value={size} onChange={setSize} />
            <Counter id="vtmDmHunger" label={vtmT('hungerDiceCount')} value={hunger} onChange={setHunger} max={MAX_HUNGER} />
            <div className="vtmField">
              <label className="vtmFieldLabel" htmlFor="vtmDmDifficulty">{vtmT('difficulty')}</label>
              <input id="vtmDmDifficulty" type="number" min="0" inputMode="numeric" placeholder={vtmT('difficultyUnknown')} value={difficulty} onChange={event => setDifficulty(event.target.value)} />
            </div>
          </div>
          <label className="vtmToggle" htmlFor="vtmDmHidden">
            <input id="vtmDmHidden" type="checkbox" checked={hidden} onChange={event => setHidden(event.target.checked)} />
            <span>{vtmT('hiddenRoll')}</span>
          </label>
          <div className="vtmRollSummary">
            <span>{vtmT('poolSummary', {size, hunger: Math.min(hunger, size)})}</span>
            <button type="submit" className="vtmButton primary" disabled={busy}>{vtmT('rollAction')}</button>
          </div>
        </form>
      )}
      {record && (
        <div className="vtmRollOutcome">
          <VtmPoolResult record={record} />
          <div className="vtmModalActions">
            <button type="button" className="vtmButton" onClick={() => setRecord(null)}>{vtmT('newRoll')}</button>
            <button type="button" className="vtmButton ghost" onClick={onClose}>{vtmT('close')}</button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default VtmDmRollModal;
